// =============================================
// TROUBLESHOOTING DATA & DIAGNOSTIC MATRIX
// Merged aviation symptoms & diagnostic matrix
// =============================================

export const SYMPTOMS = [
    {
        id: 'fuel-dripping',
        title: 'Fuel Dripping from Carburetor Throat After Shutdown',
        icon: '🚰',
        richLean: 'Rich / Flooding',
        primarySuspect: 'Worn or defective float needle valve and seat.',
        secondarySuspect: 'A punctured, sunken float (brass) or a saturated/heavy composite float.',
        visualAction: 'simulate_float_failure',
        mechanic: 'The float fails to rise, the needle valve stays open, and fuel overflows the main discharge nozzle.',
        checks: [
            'Inspect needle valve for scoring/deformation.',
            'Perform float immersion test to check for leaks.',
            'Confirm fuel pump pressure is within limits (too high can push past the needle).'
        ]
    },
    {
        id: 'engine-hesitates-decel',
        title: 'Engine Hesitates or Quits on Deceleration (Throttling Back)',
        icon: '🛑',
        richLean: 'Temporary Rich Stumble',
        primarySuspect: 'Defective accelerator pump leather boot (specifically on MSA MA-4SPA models).',
        visualAction: 'simulate_accel_boot_fail',
        mechanic: 'If the pump boot is folded or worn, fuel gets to the wrong side of the pump plunger. When throttled back rapidly, this trapped fuel is forced out of the vent, causing a temporary rich stumble.',
        checks: [
            'Open pump well and inspect boot for folding/cracking.',
            'Ensure spring tension on the pump return is correct.'
        ]
    },
    {
        id: 'no-rpm-rise',
        title: 'No RPM Rise at Idle Cut-Off',
        icon: '🔩',
        richLean: 'Lean Idle',
        primarySuspect: 'Idle mixture is set too lean.',
        secondarySuspect: 'An induction system leak (unmetered air entering the intake manifold past the carburetor).',
        visualAction: 'simulate_lean_idle',
        mechanic: 'Adjust the idle mixture screw counter-clockwise (richer) until a 25 to 50 RPM rise is observed just before the engine dies during cut-off.',
        checks: [
            'Perform ground check: slowly lean mixture from idle; note RPM needle activity.',
            'Spray soapy water on manifold joints to check for leaks.'
        ]
    },
    {
        id: 'dieseling-idle',
        title: 'Engine Continues to Run (Diesels) in Idle Cut-Off',
        icon: '🔄',
        richLean: 'Leakage Past Valve',
        primarySuspect: 'The mixture control valve is not seating perfectly.',
        visualAction: 'simulate_mixture_leak',
        mechanic: 'If the rotary valve or plunger is scored or not lapped correctly to its seat, fuel continues to seep past it even when the cockpit lever is pulled fully aft.',
        checks: [
            'Remove mixture control assembly and inspect for scoring.',
            'Lap the valve and seat to ensure absolute cutoff.'
        ]
    },
    {
        id: 'hesitates-accel',
        title: 'Engine Hesitates/Stumbles on Rapid Acceleration',
        icon: '💨',
        richLean: 'Lean Stumble',
        primarySuspect: 'Failing accelerator pump or clogged pump discharge nozzle.',
        visualAction: 'simulate_accel_fail',
        mechanic: 'The throttle butterfly opens instantly, flooding the engine with air, but the fuel lags behind due to its greater mass. Without the pump shooting a raw stream of fuel to bridge the gap, the mixture goes momentarily, dangerously lean.',
        checks: [
            'Confirm fuel stream is visible in carburetor throat during rapid throttle check.',
            'Clean discharge nozzle with compressed air.'
        ]
    },
    {
        id: 'hard-start-cold',
        title: 'Hard Starting — Cold Engine',
        icon: '❄️',
        richLean: 'Usually Lean',
        primarySuspect: 'Accelerator pump not priming; no initial fuel shot.',
        secondarySuspect: 'Needle valve stuck closed or intake strainer clogged.',
        visualAction: 'simulate_accel_fail',
        mechanic: 'In cold weather, fuel volatility is low. The engine requires an extra-rich prime to fire.',
        checks: [
            'Confirm boost pump is ON.',
            'Crack throttle and pump twice to prime intake manifold.'
        ]
    },
    {
        id: 'high-consumption',
        title: 'High Fuel Consumption & Sooty Plugs',
        icon: '📈',
        richLean: 'Over-Rich',
        primarySuspect: 'High float level or leaking needle.',
        secondarySuspect: 'Main air bleed blocked or economizer stuck open.',
        visualAction: 'simulate_float_failure',
        mechanic: 'If air bleeds are blocked, fuel delivers as liquid droplets rather than fine emulsion, resulting in heavy richness.',
        checks: [
            'Inspect air bleeds for dirt or mud-dauber nests.',
            'Check economizer linkage for binding.'
        ]
    },
    {
        id: 'high-egt',
        title: 'High EGT / Possible Detonation',
        icon: '🌡️',
        richLean: 'Dangerously Lean',
        primarySuspect: 'Main jet partially blocked or economizer stuck closed.',
        secondarySuspect: 'Bowl vent blocked causing vacuum suction fighting fuel delivery.',
        visualAction: 'simulate_mixture_leak',
        mechanic: 'Lean mixtures burn slower and hotter, raising cylinder and exhaust gas temperatures significantly.',
        checks: [
            'Remove main jet and inspect for obstructions.',
            'Verify bowl vent for positive ventilation.'
        ]
    }
];

export const MIXTURE_DIAGNOSIS = [
    {
        title: 'How to Tell: RPM Response to Mixture Change',
        description: 'From cruise, slowly lean the mixture. If RPM rises slightly before falling, you have passed through best economy — this is normal. If RPM drops immediately with any leaning, mixture was already at or leaner than best economy.',
        suspects: 'Disconnected cable, worn mixture valve, back-suction system leak.'
    },
    {
        title: 'How to Tell: EGT Change and Trend',
        description: 'EGT rises as you lean toward peak. Past peak, EGT drops as the mixture becomes too lean to sustain complete combustion.',
        suspects: 'Blocked main jet, restricted fuel supply, economizer stuck closed.'
    },
    {
        title: 'How to Tell: Spark Plug Appearance',
        description: 'Rich operation deposits black, sooty carbon on plugs. Proper mixture leaves plugs light grey to light tan. Lean operation leaves plugs very white/blistered.',
        suspects: 'Rich plugs: high float level, blocked air bleed. Lean plugs: low fuel pressure, restricted supply.'
    }
];

export function renderSymptomList(container) {
    container.innerHTML = `
        <h3>Diagnostic Matrix</h3>
        <p class="subtitle">Select a symptom to identify failure modes</p>
        <div id="symptom-matrix-grid">
            ${SYMPTOMS.map(s => `
                <div class="symptom-action-card" data-symptom-id="${s.id}">
                    <div class="symptom-title">${s.icon} ${s.title}</div>
                    <div class="symptom-suspect"><strong>Suspect:</strong> ${s.primarySuspect}</div>
                </div>
            `).join('')}
        </div>
    `;
}

export function renderDiagnosis(container, symptomId) {
    const symptom = SYMPTOMS.find(s => s.id === symptomId);
    if (!symptom) return;

    container.innerHTML = `
        <div class="diagnosis-detail glass-panel">
            <div class="diag-header">
                <h3>Diagnostic Analysis: ${symptom.title}</h3>
                <span class="rich-lean-badge ${symptom.richLean.toLowerCase().includes('lean') ? 'lean' : 'rich'}">${symptom.richLean}</span>
            </div>
            
            <div class="diag-grid-split">
                <div class="diag-suspect-box">
                    <label>AME Primary Suspect</label>
                    <p><strong>${symptom.primarySuspect}</strong></p>
                    ${symptom.secondarySuspect ? `<p><em>Secondary:</em> ${symptom.secondarySuspect}</p>` : ''}
                </div>
                
                <div class="diag-mechanic-box">
                    <label>The Mechanic / Reasoning</label>
                    <p>${symptom.mechanic}</p>
                </div>
            </div>

            <div class="diag-checks-box">
                <label>📋 Diagnostic Bench-Checks</label>
                <ul>
                    ${symptom.checks.map(c => `<li>${c}</li>`).join('')}
                </ul>
            </div>
            
            <div class="visual-feedback-hint">
                <p>💡 <em>A 3D simulation of this failure is now active on the model.</em></p>
            </div>
        </div>
    `;
}

export function renderMixtureDiagnosis(container) {
    container.innerHTML = `
        <div class="mixture-diag-grid">
            ${MIXTURE_DIAGNOSIS.map(item => `
                <div class="mixture-diag-card">
                    <h4>${item.title}</h4>
                    <p>${item.description}</p>
                    <p style="margin-top:6px;color:#ff9800;font-size:11px;"><strong>Likely suspects:</strong> ${item.suspects}</p>
                </div>
            `).join('')}
        </div>
    `;
}
